import {ChangeDetectionStrategy, Component, inject} from '@angular/core';
import {ReactiveFormsModule} from '@angular/forms';
import {FormDialog, FormDialogDefinition} from '@aviary/dialogs/form-dialog/form-dialog.types';
import {FormDialogComponent} from '@aviary/dialogs/form-dialog/form-dialog.component';
import {ExtendedFormGroup} from '@aviary/forms/extensions/extended-form-group/extended-form-group';
import {buildFormGroup} from '@aviary/forms/form.helpers';
import {FormRowComponent} from '@aviary/forms/fields/form-row/form-row.component';
import {CheckboxFormFieldComponent} from '@aviary/forms/fields/checkbox-form-field/checkbox-form-field.component';
import {InputFormFieldComponent} from '@aviary/forms/fields/input-form-field/input-form-field.component';
import {SelectFormFieldComponent} from '@aviary/forms/fields/select-form-field/select-form-field.component';
import {TextareaFormFieldComponent} from '@aviary/forms/fields/textarea-form-field/textarea-form-field.component';
import {SelectPipe} from '@aviary/pipes/select.pipe';
import {createStringSelector} from '@aviary/store/store.helpers';
import {BranchSelectors} from '@store/branches/branch.selectors';
import {FALLBACK_CREATE_ITEM_REQUEST_VALUE} from '@store/item-requests/item-requests.fallback';
import {PatronSelectors} from '@store/patron/patron.selectors';
import {CreateItemRequestValue, PRINT_TYPE_SELECT_OPTIONS} from '@store/item-requests/item-requests.types';
import {ItemRequestsValidators} from '@store/item-requests/item-requests.validators';
import {OrganizationSelectors} from '@store/organization/organization.selectors';
import {isUndefined} from '@aviary/store/common/typing.helpers';

@Component({
  selector: 'px-create-item-request-dialog',
  templateUrl: './create-item-request.dialog.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormDialogComponent, ReactiveFormsModule, SelectFormFieldComponent, InputFormFieldComponent, FormRowComponent, SelectPipe, CheckboxFormFieldComponent, TextareaFormFieldComponent]
})
export class CreateItemRequestDialog implements FormDialog<undefined, CreateItemRequestValue> {
  public readonly dialogData = undefined;
  public readonly organizationSelectors = OrganizationSelectors.Auth;
  public readonly pickupBranchSelector = BranchSelectors.PickupLocations.pickupLocationsSelectOptions;
  public readonly PRINT_TYPE_SELECT_OPTIONS = PRINT_TYPE_SELECT_OPTIONS;
  public readonly formGroup: ExtendedFormGroup = buildFormGroup(
    FALLBACK_CREATE_ITEM_REQUEST_VALUE,
    inject(ItemRequestsValidators).createItemRequest
  );

  public readonly dialogDefinition: FormDialogDefinition<CreateItemRequestValue> = {
    title: 'Request an Item',
    description: createStringSelector(
      OrganizationSelectors.Auth.copyOverrides, PatronSelectors.AccountStatus.loaded, PatronSelectors.AccountStatus.status,
      (copy, loaded, status) => {
        if (copy.PATRON_ITEM_REQUEST_LIMIT_OVERRIDE) return copy.PATRON_ITEM_REQUEST_LIMIT_OVERRIDE;
        if (!loaded || isUndefined(status.itemRequestUsage)) return 'Monthly item request usage is currently unavailable.';
        const {monthlyCount, monthlyLimit} = status.itemRequestUsage;
        return `You’ve made ${monthlyCount} of ${monthlyLimit} item requests this month. You have ${Math.max(0, monthlyLimit - monthlyCount)} remaining.`;
      }
    ),
    confirmButtonLabel: 'Submit',
    formGroup: this.formGroup
  };
}
